import { getChatApiRoute } from '../ChatBackendUtils'
import { isNetworkOffline } from '../NetworkUtils'

export async function getConnections(token: string): Promise<Array<any>> {
  const response = await fetch(getChatApiRoute('api/v1/connections'), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`Failed to fetch connections: ${body}`)
  }

  const body = await response.json()

  const data = body.data as any[]

  return data
}

export async function isProviderConnected(provider: string, token: string): Promise<boolean> {
  try {
    const connections = await getConnections(token)
    return connections.some((connection) => connection.providerName === provider)
  } catch (error) {
    console.error('Failed to check provider connection:', error)

    // Check if it's a network connectivity issue
    if (isNetworkOffline()) {
      console.log('Network is offline, assuming provider is not connected')
    }

    // Return false when there's an error fetching connections
    return false
  }
}

export function checkGoogleCalendarConnected(connections: Array<any>): boolean {
  // A personal `google` row with calendar scope, or the org-provided
  // `google-calendar` entry (domain-wide delegation — no personal row exists).
  // An expired row has no working credential, so it doesn't count as connected.
  return connections.some(
    (connection) =>
      (connection.providerName === 'google' ||
        (connection.providerName === 'google-calendar' && connection.metadata?.orgProvided)) &&
      !connection.hasExpired &&
      connection.scopes &&
      connection.scopes.includes('https://www.googleapis.com/auth/calendar.events'),
  )
}

export function checkMicrosoftCalendarConnected(connections: Array<any>): boolean {
  return connections.some(
    (connection) =>
      connection.providerName === 'microsoft' &&
      !connection.hasExpired &&
      connection.scopes &&
      connection.scopes.includes('Calendars.ReadWrite'),
  )
}

export async function isGoogleCalendarConnected(token: string): Promise<boolean> {
  const connections = await getConnections(token)
  return checkGoogleCalendarConnected(connections)
}

export async function isMicrosoftCalendarConnected(token: string): Promise<boolean> {
  const connections = await getConnections(token)
  return checkMicrosoftCalendarConnected(connections)
}

export async function disconnectDefaultProvider(provider: string, token: string): Promise<void> {
  const connections = await getConnections(token)

  const connection = connections.find((connection) => connection.providerName === provider)

  if (!connection) {
    throw new Error(`No connection found for provider: ${provider}`)
  }

  const response = await fetch(getChatApiRoute(`api/v1/connections/${connection.id}`), {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`Failed to disconnect provider: ${body}`)
  }
}

/**
 * The connection providers that back the single Ashby integration (mirrors
 * `ASHBY_PROVIDER_FAMILY` on the backend). Dependents are ordered before the
 * `ashby-mcp` OAuth base because deleting the base cascades to them. `ashby`
 * holds the optional org-API-key upgrade, while `ashby-client` is a retired
 * local-session marker kept only so disconnect still sweeps legacy rows. The
 * settings UI shows them all as one "Ashby" card.
 */
export const ASHBY_CONNECTION_PROVIDERS = ['ashby', 'ashby-client', 'ashby-mcp'] as const

/**
 * Disconnects every connection row whose provider is in `providers`, in the
 * order supplied. Unlike `disconnectDefaultProvider`, absent provider members
 * are not an error.
 */
export async function disconnectProviders(providers: ReadonlyArray<string>, token: string): Promise<void> {
  const connections = await getConnections(token)
  const matchingConnections = connections
    .filter((connection) => providers.includes(connection.providerName))
    .sort((left, right) => providers.indexOf(left.providerName) - providers.indexOf(right.providerName))

  for (const connection of matchingConnections) {
    const response = await fetch(getChatApiRoute(`api/v1/connections/${connection.id}`), {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (!response.ok) {
      const body = await response.text()
      throw new Error(`Failed to disconnect provider ${connection.providerName}: ${body}`)
    }
  }
}

/**
 * Re-resolve the caller's own Ashby identity on their ORGANIZATION's shared
 * connection. Self-serve only — cannot remove the org connection or touch an
 * admin-pinned mapping. 'blocked' is a DEFINITIVE no-match ("ask your admin");
 * an unreachable directory throws instead (the server resolves before
 * swapping, so a working mapping survives the outage) — callers must catch
 * and present it as a retryable failure, not as blocked.
 */
export async function resyncOrgProvidedAshby(token: string): Promise<'resolved' | 'blocked'> {
  const response = await fetch(getChatApiRoute('api/v1/connections/ashby/org-resync'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok) {
    const body = await response.text()
    throw new Error(`Failed to resync the organization Ashby mapping: ${body}`)
  }
  const json = (await response.json()) as { data?: { status?: string } }
  const status = json.data?.status
  // Never default an unknown shape to 'resolved' — "your mapping is up to
  // date" on a malformed body would mask a real problem. Throwing routes it
  // to the caller's retryable-error state instead.
  if (status !== 'resolved' && status !== 'blocked') {
    throw new Error(`Unexpected organization Ashby resync status: ${JSON.stringify(status)}`)
  }
  return status
}
